import { CategoriesSlider } from "./categoryList";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useEffect, useRef } from "react";
import { BackBarButtonMini } from "../common/backBarButton";
import smoothScroll from "../../utils/animateScroll";
import { useCategoryContentList } from "../../utils/hooks/useCategory";
import { useInputMode } from "../../utils/hooks/useInputMode";

// ============================================================================
// STYLED COMPONENTS (Legacy CSS compatible)
// ============================================================================

var ContentWrapper = styled.div`
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  overflow: hidden;
  height: 100vh;
  padding: 2rem 4rem;
  padding-bottom: 0;
`;

var ScrollingRows = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  width: 100%;
  position: relative;
  height: calc(100vh - 64px);
  padding-bottom: 20vh;
`;

var Sentinel = styled.div`
  height: 1px;
`;

var HeaderRow = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-bottom: 1.25rem;
`;

var PageTitle = styled.h2`
  color: white;
  font-size: 2.125rem;
  font-weight: 700;
  margin: 0;
  margin-right: 0.75rem;
  font-family: "AnjomanMax", serif;
`;

var EmptyMessage = styled.div`
  background-color: #212121;
  padding: 1.25rem;
  border-radius: 0.75rem;
  font-size: 1.125rem;
`;

var EmptyText = styled.span`
  color: white;
  font-family: "AnjomanMax", serif;
`;

var LoadingContainer = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  height: 3.5rem;
`;

var LoadingText = styled.span`
  color: white;
  font-family: "AnjomanMax", serif;
`;

// ============================================================================
// COMPONENT
// ============================================================================

var CategoriesContent = function (props) {
  var data = props.data;
  var slug = props.slug;
  var contentKind = props.contentKind;

  var focusableResult = useFocusable();
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  var categoryHook = useCategoryContentList({
    fetchDelay: 0,
    slug: slug,
    initialPage: 1,
    pageSize: 20,
    // When contentKind is set, skip unfiltered initial data and auto-fetch from content endpoint
    autoInit: !!contentKind,
    initialData: contentKind ? [] : (data?.contents?.[0]?.data || []),
    lazyLoading: false,
    contentKind: contentKind,
  });

  var items = categoryHook.items;
  var hasMore = categoryHook.hasMore;
  var onLoadMore = categoryHook.onLoadMore;
  var isLoading = categoryHook.isLoading;

  var sentinelRef = useRef();
  var { isMouseFocus } = useInputMode();

  var loadMoreRef = useRef(null);
  loadMoreRef.current = function () {
    if (hasMore && !isLoading) {
      onLoadMore();
    }
  };

  var onRowFocus = useCallback(
    function (focusProps) {
      if (isMouseFocus) return;
      var y = focusProps.y;
      var height = focusProps.height;

      // Get the current scroll position of the container
      var currentScroll = ref.current ? ref.current.scrollTop : 0;

      // Calculate how much we need to scroll from current position
      var comfortZone = 200; // pixels from top where element should be visible

      if (y > height - 300) {
        // Element is near or below bottom of viewport - scroll down
        var scrollAmount = y - comfortZone;
        var targetScroll = currentScroll + scrollAmount;
        smoothScroll(ref, targetScroll, 300);
      } else if (y < comfortZone) {
        // Element is above comfort zone - scroll up
        var scrollAmountUp = y - comfortZone;
        var targetScrollUp = Math.max(0, currentScroll + scrollAmountUp);
        smoothScroll(ref, targetScrollUp, 300);
      }

      if (y >= height * 0.7) {
        loadMoreRef.current?.();
      }
    },
    [ref, isMouseFocus]
  );

  useEffect(
    function () {
      var scrollContainer = ref.current;
      var sentinel = sentinelRef.current;
      if (!scrollContainer || !sentinel) return;

      var marginPx = Math.round(window.innerHeight * 0.1);
      var observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          if (entry.isIntersecting) {
            loadMoreRef.current?.();
          }
        },
        { root: scrollContainer, rootMargin: '0px 0px ' + marginPx + 'px 0px', threshold: 0.1 }
      );

      observer.observe(sentinel);

      return function () {
        observer.unobserve(sentinel);
      };
    },
    [items.length]
  );

  return (
    <ContentWrapper>
      <FocusContext.Provider value={focusKey}>
        <HeaderRow>
          <BackBarButtonMini />
          <PageTitle>{data.result.name}</PageTitle>
        </HeaderRow>
        <ScrollingRows ref={ref}>
          {items.length > 0 ? (
            <CategoriesSlider
              rowTitle=""
              movies={items}
              onFocus={onRowFocus}
            />
          ) : !isLoading ? (
            <EmptyMessage>
              <EmptyText>محتوایی یافت نشد</EmptyText>
            </EmptyMessage>
          ) : null}
          {isLoading && (
            <LoadingContainer>
              <LoadingText>در حال بارگذاری...</LoadingText>
            </LoadingContainer>
          )}
          <Sentinel ref={sentinelRef} />
        </ScrollingRows>
      </FocusContext.Provider>
    </ContentWrapper>
  );
};

export default CategoriesContent;
